import React, { useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { useFlashMessage } from '../context/FlashMessageContext';
import { API_CONFIG } from '../config/api';
import SyberKeyWordmark from '../components/SyberKeyWordmark';
import './Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useContext(AuthContext);
  const { showSuccess, showError, showInfo } = useFlashMessage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    showInfo('Sending login link...');
    
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/passwordless/initiate`, { email });
      
      if (response.data.success) {
        setEmailSent(true);
        showSuccess('Login link sent! Please check your email and click the link to sign in.');
        setMessage(response.data.message);
      } else if (response.data.success === false && response.data.message) {
        showError(response.data.message);
      } else {
        showError('Failed to send login link. Please try again.');
      }
    } catch (error) {
      console.error('Login initiation failed:', error);
      
      if (error.response && error.response.status >= 500) {
        showError('Server error occurred. Please try again later.');
      } else {
        showError('Failed to send login link. Please check your internet connection and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open callback URL in new tab when arriving from successful onboarding
  React.useEffect(() => {
    const callbackurl = location.state?.callbackurl;
    if (callbackurl && (callbackurl.startsWith('http://') || callbackurl.startsWith('https://'))) {
      window.open(callbackurl, '_blank', 'noopener,noreferrer');
    }
  }, [location.state?.callbackurl]);

  // Check for URL parameters when component loads (for successful login redirects)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const userParam = urlParams.get('user');
    
    if (token && userParam) {
      try {
        const user = JSON.parse(decodeURIComponent(userParam));
        login(token, user);
        showSuccess(`Welcome back, ${user.fullName}! Redirecting to dashboard...`);
        
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Redirect to dashboard
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } catch (error) {
        console.error('Failed to parse login parameters:', error);
        showError('Login verification failed. Please try logging in again.');
      }
    }
  }, [login, navigate, showSuccess, showError]);

  return (
    <div className="login-container">
      <form onSubmit={handleSubmit} className="login-form">
        <h1 className="page-title-with-brand">
          <SyberKeyWordmark />
          <span className="page-title-with-brand__suffix"> Login</span>
        </h1>
        {message && <div className="message">{message}</div>}
        
        {!emailSent ? (
          <>
            <p>Enter your registered email address and we'll send you a secure login link.</p>
            <div className="form-group">
              <label>Email:</label>
              <input 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                placeholder="Enter your registered email address"
                required 
              />
            </div>
            <button 
              type="submit" 
              disabled={isSubmitting}
              className={isSubmitting ? 'loading' : ''}
            >
              {isSubmitting ? 'Sending Link...' : 'Send Login Link'}
            </button>
          </>
        ) : (
          <div className="email-sent-message">
            <h2>📧 Check Your Email</h2>
            <p>We've sent a secure login link to <strong>{email}</strong></p>
            <p>Click the link in your email to complete the login process.</p>
            <p><small>The link will expire in 15 minutes for security.</small></p>
            
            <button 
              type="button" 
              onClick={() => {
                setEmailSent(false);
                setEmail('');
                setMessage('');
              }}
              className="secondary-button"
            >
              Use Different Email
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

export default Login;
